import Router from 'next/router';
import { contains, findIndex, propEq, uniq } from 'ramda';
import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { ISupplierAdmin } from '@bridebook/models/source/models/Suppliers/Admin.types';
import { ILeadIntentOption } from '@bridebook/models/source/models/Suppliers/Leads.types';
import type { IOffer } from '@bridebook/models/source/models/Suppliers/Offers.types';
import { IPhoto } from '@bridebook/models/source/models/Suppliers/Photos.types';
import { IVideo } from '@bridebook/models/source/models/Suppliers/Videos.types';
import { getSupplierUrl, ValidationError } from '@bridebook/toolbox/src';
import { fetchy } from '@bridebook/web/lib/fetchy';
import { FetchSupplierDataResponse } from '@bridebook/toolbox/src/supplier/fetch-supplier-data';
import { IUISupplier, Slug } from '@bridebook/toolbox/src/types';
import { getPricingAiProperties } from '@bridebook/toolbox/src/supplier/utils/pricing';
import { PRICING_SECTION_ID } from '@bridebook/web/app-shared/components/supplier/sections/ids';
import { IS3File } from '@bridebook/ui';
import { formatReviewFormError } from '@bridebook/web/app-shared/lib/reviews/utils';
import { ISupplierPhoto, Video } from '@bridebook/web/app-shared/lib/supplier/supplier-types';
import {
  IFeedbackSerialized,
  SupplierCardInView,
  ViewedEndScreen,
} from '@bridebook/web/app-shared/lib/supplier/types';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { toggleSnackbar } from '@bridebook/web/lib/bbcommon/actions';
import { enquiryFormToggle, setEnquirySupplier } from '@bridebook/web/lib/enquiries/actions';
import { getI18n } from '@bridebook/web/lib/i18n/getI18n';
import { format } from '@bridebook/web/lib/i18n/utils/get-date-fns-format';
import { OfferTypes } from '@bridebook/web/lib/offers/constants';
import { SearchActionTypes } from '@bridebook/web/lib/search/action-types';
import { getSearchList } from '@bridebook/web/lib/search/selectors';
import { buildUISupplier } from '@bridebook/web/lib/shortlist/utils';
import { ANALYTICS_BY_SECTION_ID } from '@bridebook/web/lib/supplier/data/supplier-analytics-by-section-id';
import {
  getSupplierPricingGroupedByYearQuery,
  ISupplierQueryParams,
} from '@bridebook/web/lib/supplier/hooks/query/use-supplier';
import { getSupplierTypeSlug, getYesterday } from '@bridebook/web/lib/supplier/utils';
import { TrackingEvent } from '@bridebook/web/lib/track-utils/tracking-event';
import {
  EnquiryFormInfoProps,
  IDeps,
  MoodBoardMediaType,
  ReviewFormFields,
  WatchedSupplierVideoLocations,
} from '@bridebook/web/lib/types';
import { toggleSpecialOfferPopup } from '@bridebook/web/lib/ui/actions';
import { getSupplierFriendlyUrlId, noopAction } from '@bridebook/web/lib/utils';
import { assertState } from '@bridebook/web/lib/utils/assertState';
import {
  IViewedSupplierAnalyticsAction,
  SupplierActions,
  SupplierPricingBrochureAnalyticsLocation,
  SupplierPricingBrochureAnalyticsSection,
  type SupplierProfileSharedLinkAnalyticsLocation,
  type SupplierProfileSharedLinkAnalyticsSection,
} from './actions-types';
import { getCurrentSupplier, getCurrentSupplierPhotosCount, getSupplierPhotos } from './selectors';
import { IPricingAiProperties, SupplierCategory } from './types';
import { resolveSupplierBucketSetting } from '@bridebook/toolbox/src/gazetteer/resolve-supplier-bucket-setting';
import gazetteer from '@bridebook/toolbox/src/gazetteer';

export const toggleSupplierAccordion =
  (
    name: string,
    opened: boolean = false,
    method: string | null = 'accordion',
    ui: boolean = true,
  ) =>
  ({ getState, dispatch }: IDeps) => {
    const openedList = [...getState().supplier.accordions];
    const open = opened || !contains(name, openedList);

    if (open) {
      openedList.push(name);
      // analytics
      const section = name.replace('-accordion', '');
      if (ui) {
        dispatch({
          type: SupplierActions.REVEALED_MORE_CONTENT_ON_SUPPLIER_PROFILE_ANALYTICS,
          payload: { method, section },
        });
      }
    } else {
      const index = openedList.indexOf(name);
      openedList.splice(index, 1);
    }

    return {
      type: SupplierActions.TOGGLE_SUPPLIER_ACCORDION,
      payload: uniq(openedList),
    };
  };

export const clickShowMoreReview = (review: Partial<IFeedbackSerialized>) => ({
  payload: {
    selectedReview: review.id,
  },
  type: SupplierActions.CLICKED_SHOW_MORE_REVIEW,
});

export const toggleSupplierReviewsPage =
  (show: boolean, selectedReview?: string | null) =>
  ({ dispatch }: IDeps) => {
    if (show) {
      if (selectedReview) {
        dispatch({
          type: SupplierActions.CLICKED_SEE_ALL_REVIEWS,
        });
      } else {
        dispatch({
          type: SupplierActions.CLICKED_SEE_REVIEW,
          payload: {
            selectedReview,
          },
        });
      }
    }

    return {
      type: SupplierActions.SUPPLIER_REVIEWS_TOGGLE,
      payload: {
        show,
        selectedReview: selectedReview || null,
      },
    };
  };

export const viewedSupplierAnalytics =
  ({
    supplier,
    refSupplier,
    viewedLocation,
    profileViewedType,
    hasViewingDateAvailable,
  }: IViewedSupplierAnalyticsAction['payload']) =>
  ({ cordovaTracker }: IDeps) => {
    if (supplier.type === 'venue' || supplier.type[0] === 'venue') {
      cordovaTracker.trackOnce(TrackingEvent.ViewedSupplierProfile, supplier);
    }
    return {
      type: SupplierActions.VIEWED_SUPPLIER_PROFILE_ANALYTICS,
      payload: {
        supplier,
        refSupplier,
        viewedLocation,
        profileViewedType,
        hasViewingDateAvailable,
      },
    };
  };

export const fetchFeedbackPromise = (id: string, page = 1, itemsOnPage = 0, locale?: string) =>
  fetchy().authenticated.get('/api/supplier/feedback/[id]', {
    id,
    page,
    locale,
    itemsOnPage,
  });

export const fetchTopCompactFeedbackPromise = (id: string, locale?: string) =>
  fetchy().authenticated.get('/api/supplier/top-feedback-compact/[id]', {
    id,
    locale,
  });

export const fetchSupplierData = (publicId: string, locale?: string) =>
  fetchy().authenticated.get('/api/supplier/[id]/data', {
    id: publicId,
    locale,
  });

export const fetchSupplierReviewRating = (id: string) =>
  fetchy().authenticated.get('/api/supplier/[id]/statistics/reviews-rating', {
    id,
  });

export const fetchSupplierStart = (query: ISupplierQueryParams) => ({
  type: SupplierActions.FETCH_SUPPLIER_START,
  payload: { query },
});

export const fetchSupplierError = (errorCode: number) => () => ({
  type: SupplierActions.FETCH_SUPPLIER_ERROR,
  payload: { errorCode },
});

export const setSupplierDeleted = (deleted?: boolean) => ({
  type: SupplierActions.SET_SUPPLIER_DELETED,
  payload: { deleted },
});

export const fetchSupplierSuccess =
  (supplier: ISupplier, reviewsPage: boolean = false) =>
  ({ dispatch, getState }: IDeps) => {
    const state = getState();

    const { isMobile } = getScreenType(state);

    const asyncAction = async () => {
      dispatch({
        type: SupplierActions.FETCH_SUPPLIER_SUCCESS,
        payload: { supplier },
      });
      if (!isMobile && !reviewsPage) {
        dispatch(toggleSupplierAccordion('faq-accordion', true, undefined, false));
      }
    };
    asyncAction();
    return noopAction();
  };

export const viewedSupplierProfileSectionAnalyticsSuccess =
  (viewedMethod: IViewedSupplierProfileSectionAnalytics['viewedMethod']) =>
  ({ dispatch }: IDeps) => {
    const [, autoscrollTargetId] = Router.asPath.split('#');

    if (autoscrollTargetId) {
      dispatch(
        viewedSupplierProfileSectionAnalyticsWithPricing({
          viewedMethod,
          viewedSection:
            ANALYTICS_BY_SECTION_ID[autoscrollTargetId as keyof typeof ANALYTICS_BY_SECTION_ID],
        }),
      );
    }

    return noopAction();
  };

export function gotoNextSupplier() {
  return ({ getState, dispatch }: IDeps) => {
    const state = getState();
    const list = getSearchList(state);
    const supplierId = getCurrentSupplier(state)?.id;
    const index = findIndex(propEq('id', supplierId), list);
    const nextSupplier = list[index + 1];

    if (nextSupplier) {
      Router.push(getSupplierUrl(nextSupplier), undefined, { scroll: true });
    }

    dispatch({
      type: SupplierActions.CLICKED_NEXT_SUPPLIER_PROFILE_ANALYTICS,
      payload: { nextSupplier },
    });

    return {
      type: SupplierActions.GOTO_NEXT_SUPPLIER,
    };
  };
}

export interface FetchSupplierPromiseQuery {
  id: string;
  supplier?: string;
  slug: Slug;
}

export const clickedSupplierSocialMediaAnalytics = (method: string) => ({
  type: SupplierActions.CLICKED_SUPPLIER_SOCIAL_MEDIA_ANALYTICS,
  payload: { method },
});

export const usedQuickNavigationAnalytics = (method: string, section: string) => ({
  type: SupplierActions.USED_QUICK_NAVIGATION_ON_SUPPLIER_PROFILE_ANALYTICS,
  payload: { method, section },
});

export const revealedMoreContentAnalytics = (method: string, section: string) => ({
  type: SupplierActions.REVEALED_MORE_CONTENT_ON_SUPPLIER_PROFILE_ANALYTICS,
  payload: { method, section },
});

export const scrolledSupplierMediaGalleryAnalytics = (supplierPhotoUrls: string[]) => ({
  type: SupplierActions.SCROLLED_SUPPLIER_MEDIA_GALLERY_ANALYTICS,
  payload: { supplierPhotoUrls },
});

export const toggledSupplierMediaGalleryAnalytics = (
  isGalleryOpened: boolean,
  toggledFrom?: string,
) => ({
  type: SupplierActions.TOGGLED_SUPPLIER_MEDIA_GALLERY_ANALYTICS,
  payload: { isGalleryOpened, toggledFrom },
});

export const viewedMoodBoardAnalytics = (mediaType: MoodBoardMediaType) => ({
  type: SupplierActions.VIEWED_MOOD_BOARD_ANALYTICS,
  payload: { mediaType },
});

export const viewedEndScreenOnSupplierCarouselAnalytics = ({
  imagesCount,
  location,
  firstImageUrl,
  searchView,
  supplierId,
}: ViewedEndScreen) => ({
  type: SupplierActions.VIEWED_END_SCREEN_ON_SUPPLIER_CAROUSEL_ANALYTICS,
  payload: { imagesCount, location, firstImageUrl, searchView, supplierId },
});

export const usedSearchCarouselAnalytics = ({
  numberOfPhotos,
  location,
  supplier,
  supplierPhotoUrls,
}: {
  numberOfPhotos: number;
  location: 'search' | 'supplierProfile';
  supplier: IUISupplier;
  supplierPhotoUrls: string[];
}) => ({
  type: SearchActionTypes.USED_SEARCH_CAROUSEL_ANALYTICS,
  payload: { numberOfPhotos, location, supplier, supplierPhotoUrls },
});

export const resetReviewForm = () => () => ({
  type: SupplierActions.RESET_REVIEW_FORM,
});

export const toggleReviewForm =
  (show: boolean, method?: string) =>
  ({ dispatch, getState }: IDeps) => {
    if (!show) {
      dispatch(resetReviewForm());
    }

    const {
      weddings: { profile },
    } = getState();
    let weddingDate: string = profile.weddingDate || '';

    // extra check for future date, sets date to yesterday if it's future or today
    if (new Date(weddingDate) > getYesterday()) {
      weddingDate = format(getYesterday(), 'yyyy-MM-dd');
    }

    return {
      type: SupplierActions.TOGGLE_REVIEW_FORM,
      payload: { show, weddingDate, method },
    };
  };

export interface IReviewFormFieldEvent {
  target: {
    name: keyof ReviewFormFields;
    value: ReviewFormFields[keyof ReviewFormFields];
  };
}

export const setReviewFormField = ({ target: { name, value } }: IReviewFormFieldEvent) => ({
  type: SupplierActions.SET_REVIEW_FORM_FIELD,
  payload: { name, value },
});

export const setReviewFormRating = (stars: number) => () => ({
  type: SupplierActions.SET_REVIEW_FORM_RATING,
  payload: stars,
});

export const hideReviewFormError = () => () => ({
  type: SupplierActions.HIDE_REVIEW_FORM_ERROR,
});

export const reviewFormRecaptchaVerified = () => ({
  type: SupplierActions.REVIEW_FORM_RECAPTCHA_VERIFIED,
});

export const saveReview = (captchaToken: string) => ({
  type: SupplierActions.SAVE_REVIEW,
  payload: { captchaToken },
});

export const toggleSnackbarSuccess = () =>
  toggleSnackbar('success', getI18n().t('supplier:snackbar.reviewAdded'));

export const saveReviewSuccess =
  () =>
  ({ dispatch, getState, queryClient }: IDeps) => {
    dispatch({ type: SupplierActions.SUBMITTED_REVIEW_ANALYTICS });
    dispatch(toggleReviewForm(false));

    const state = getState();
    const supplier = getCurrentSupplier(state);
    const id = getSupplierFriendlyUrlId(supplier);

    setTimeout(() => {
      dispatch(toggleSnackbarSuccess());
      queryClient.invalidateQueries({ queryKey: ['supplier-reviews', id] });
    }, 1000);

    return {
      type: SupplierActions.SAVE_REVIEW_SUCCESS,
    };
  };

export const saveReviewError =
  (error: ValidationError) =>
  ({ dispatch }: IDeps) => {
    const i18n = getI18n();
    const err = formatReviewFormError(error, i18n.t);

    dispatch({
      type: SupplierActions.FAILED_TO_SUBMIT_REVIEW_ANALYTICS,
      payload: { message: err.message },
    });

    return {
      type: SupplierActions.SAVE_REVIEW_ERROR,
      payload: err,
    };
  };

export const uploadReviewPhoto = (photo: IS3File) => ({
  type: SupplierActions.UPLOAD_REVIEW_PHOTO,
  payload: { photo },
});

export const uploadReviewPhotoSuccess = (photo: Partial<ISupplierPhoto>) => ({
  type: SupplierActions.UPLOAD_REVIEW_PHOTO_SUCCESS,
  payload: photo,
});

export const uploadReviewPhotoError = (err: Error) => ({
  type: SupplierActions.UPLOAD_REVIEW_PHOTO_ERROR,
  payload: err,
});

export const resetSupplierAccordion = () => ({
  type: SupplierActions.RESET_SUPPLIER_ACCORDION,
});

export const supplierGalleryLoadPhotos =
  () =>
  ({ dispatch, getState }: IDeps) => {
    const state = getState();
    const supplier = getCurrentSupplier(state);
    const photos = getSupplierPhotos(state);
    const photosCount = getCurrentSupplierPhotosCount(state);

    if (supplier?.id && photos && photos.length < photosCount && photosCount > 1) {
      dispatch(fetchSupplierPhotos(supplier.id));
    }

    return {
      type: SupplierActions.SUPPLIER_GALLERY_LOAD_PHOTOS,
      payload: false,
    };
  };

export const supplierGalleryToggle =
  (index: number = 0, toggledFrom?: string) =>
  ({ getState, dispatch }: IDeps) => {
    dispatch(supplierGalleryLoadPhotos());

    const show = !getState().supplier.showGallery;

    if (show) {
      dispatch(toggledSupplierMediaGalleryAnalytics(true, toggledFrom));
    }

    return { type: SupplierActions.SUPPLIER_GALLERY_TOGGLE, payload: { show, index } };
  };

export const clickedUpNextTileAnalytics = (supplier: IUISupplier) => ({
  type: SupplierActions.CLICKED_UP_NEXT_VENUE_ANALYTICS,
  payload: { supplier },
});

export const supplierVideoModalToggle =
  (open: boolean = false, video?: Video | IVideo) =>
  ({ dispatch }: IDeps) => {
    if (open && video) {
      dispatch({
        type: SupplierActions.WATCHED_SUPPLIER_VIDEO_ANALYTICS,
        payload: { video },
      });
    }

    return {
      type: SupplierActions.SUPPLIER_VIDEO_MODAL_TOGGLE,
      payload: open,
    };
  };

interface IWatchedSupplierVideoProps {
  viewLocation: WatchedSupplierVideoLocations;
  mediaType: MoodBoardMediaType;
}

export const watchedSupplierVideoAnalytics = (
  video: Video | IVideo,
  { viewLocation, mediaType }: IWatchedSupplierVideoProps,
) => ({
  type: SupplierActions.WATCHED_SUPPLIER_VIDEO_ANALYTICS,
  payload: { video, viewLocation, mediaType },
});

export const redeemSupplierSpecialOffer =
  ({ type, offer }: { type?: OfferTypes; offer?: IOffer }) =>
  ({ dispatch, getState }: IDeps) => {
    const supplier = getState().supplier.supplier;
    dispatch(toggleSpecialOfferPopup({ offer: null, type: undefined }));

    assertState(supplier, 'loaded', 'supplier');

    setTimeout(() => {
      dispatch(setEnquirySupplier({ supplier: buildUISupplier(supplier.data) }));
      const infoProps: EnquiryFormInfoProps = {
        locationType: 'supplierProfileSpecialOffer',
        method: 'supplierProfile_specialOffer',
        contactLocation: 'supplierProfile_specialOffer',
        defaultIntent:
          type === OfferTypes.lateAvailability
            ? ILeadIntentOption.availability
            : ILeadIntentOption.pricing,
      };

      dispatch(
        enquiryFormToggle({
          show: true,
          infoProps,
        }),
      );
    }, 50);

    return {
      type: SupplierActions.REDEEM_SUPPLIER_SPECIAL_OFFER,
      payload: { type, offer },
    };
  };

// TODO: check if it's still in use
export const downloadedSupplierBrochureAnalytics = (interactedSupplierBrochureLink: string) => ({
  type: SupplierActions.DOWNLOADED_SUPPLIER_BROCHURE_ANALYTICS,
  payload: { interactedSupplierBrochureLink },
});

export const supplierCardInView = ({
  supplier,
  supplierTileOrder,
  viewedLocation,
  refSupplier,
  supplierTileType = 'tile',
  recommendationEngineUsed,
  recommendationEngineVersion,
}: SupplierCardInView) => ({
  type: SupplierActions.SUPPLIER_CARD_IN_VIEW,
  payload: {
    supplier,
    supplierTileOrder,
    viewedLocation,
    refSupplier,
    supplierTileType,
    recommendationEngineUsed,
    recommendationEngineVersion,
  },
});

export const toggleWhyBridebookModal =
  (show: boolean = false) =>
  ({ dispatch }: IDeps) => {
    dispatch(toggleWhyBridebookModalAnalytics(show));

    return {
      type: SupplierActions.TOGGLE_WHY_BRIDEBOOK_MODAL,
      payload: show,
    };
  };

export const toggleWhyBridebookModalAnalytics = (show: boolean) => ({
  type: SupplierActions.TOGGLE_WHY_BRIDEBOOK_MODAL_ANALYTICS,
  payload: show,
});

export const fetchPremiumDone = (premium: ISupplierAdmin['premium']) => ({
  type: SupplierActions.FETCH_PREMIUM_DONE,
  payload: { premium },
});

export const fetchSupplierPhotos = (supplierId: string) => ({
  type: SupplierActions.FETCH_PHOTOS,
  payload: { supplierId },
});

export const fetchSupplierPhotosDone = (photos: IPhoto[]) => ({
  type: SupplierActions.FETCH_PHOTOS_DONE,
  payload: { photos },
});

export const fetchSupplierDataDone = ({
  achievements,
  appsConnected,
  badges,
  brochures,
  fairs,
  hasBadDebtCoupleside,
  offers,
  packages,
  premium,
  questions,
  recommendations,
  relations,
  seoIndex = true,
  videos,
}: Omit<FetchSupplierDataResponse, 'supplierPackages' | 'photos'>) => ({
  type: SupplierActions.FETCH_SUPPLIER_DATA_DONE,
  payload: {
    achievements,
    appsConnected,
    badges,
    brochures,
    fairs,
    hasBadDebtCoupleside,
    offers,
    packages,
    premium,
    questions,
    recommendations,
    relations,
    seoIndex,
    videos,
  },
});

export const supplierAndCoupleCollaboratingAnalytics = ({
  collaboratingSupplierId,
  collaborationTriggerLocation,
  started = true,
}: {
  collaboratingSupplierId: string;
  collaborationTriggerLocation: 'onboarding' | 'shortlist' | 'CMS' | 'weddingWebsites';
  started?: boolean;
}) => ({
  type: SupplierActions.SUPPLIER_AND_COUPLE_COLLABORATING_ANALYTICS,
  payload: {
    collaboratingSupplierId,
    collaborationTriggerLocation,
    started,
  },
});

export const sharedSupplierProfileLinkAnalytics = (payload: {
  url: string;
  sharedLocation: SupplierProfileSharedLinkAnalyticsLocation;
  sharedSection?: SupplierProfileSharedLinkAnalyticsSection;
  appChosen?: string;
  supplier: IUISupplier | ISupplier;
}) => ({
  type: SupplierActions.SHARED_SUPPLIER_PROFILE_LINK_ANALYTICS,
  payload,
});

export const clickedSupplierCategoryTagsAnalytics = (payload: {
  supplierCategory: SupplierCategory;
}) => ({
  type: SupplierActions.CLICKED_SUPPLIER_CATEGORY_TAGS_ANALYTICS,
  payload,
});

export const mapInViewAnalytics = (payload?: {
  supplier?: IUISupplier;
  profileViewedType?: 'condensed' | 'standard';
}) => ({
  payload,
  type: SupplierActions.MAP_IN_VIEW,
});

export const mapMouseUpdAnalytics = () => ({
  type: SupplierActions.MAP_MOUSE_UP,
});

export const instagramGalleryViewedAnalytics = () => ({
  type: SupplierActions.INSTAGRAM_GALLERY_VIEWED_ANALYTICS,
});

export const instagramGalleryScrolledAnalytics = () => ({
  type: SupplierActions.INSTAGRAM_GALLERY_SCROLLED_ANALYTICS,
});

//Clicked to go to message

export interface IClickedToGoToMessageAnalytics {
  clickedLocation:
    | 'search'
    | 'supplierProfile'
    | 'loggedOutSupplierProfile'
    | 'supplierMatching'
    | 'gallery'
    | 'gallery_viewProfile'
    | 'swiping_stickyButton'
    | 'swiping_stickyButton_details'
    | 'shortlist'
    | 'priceEstimate'
    | 'showroundsAttendanceLink'
    | 'OpenDaysAttendanceLink'
    | 'inbox'
    | 'searchResults'
    | 'eventsPage'
    | 'eventsCarousel'
    | 'supplierMatching_shortlist';
  clickedSection: string;
  cta: string;
  supplier?: IUISupplier;
}
export const clickedGoToMessageAnalytics = (payload: IClickedToGoToMessageAnalytics) => ({
  type: SupplierActions.CLICKED_GO_TO_MESSAGE_ANALYTICS,
  payload,
});

export interface IViewedSupplierProfileSectionAnalytics {
  viewedMethod:
    | 'loadedProfile'
    | 'autoscrollNavigationBar'
    | 'scrollNavigation'
    | 'clicked'
    | 'automatically';
  viewedLocation?: string;
  viewedSection: (typeof ANALYTICS_BY_SECTION_ID)[keyof typeof ANALYTICS_BY_SECTION_ID];
  supplier?: IUISupplier | ISupplier;
  sectionType?: 'profile' | 'slide';
  pricingAiProperties?: IPricingAiProperties;
}

export const viewedSupplierProfileSectionAnalytics = ({
  viewedMethod,
  viewedSection,
  viewedLocation,
  supplier,
  sectionType = 'profile',
  pricingAiProperties,
}: IViewedSupplierProfileSectionAnalytics) => ({
  type: SupplierActions.VIEWED_SUPPLIER_PROFILE_SECTION_ANALYTICS,
  payload: {
    viewedMethod,
    viewedSection,
    viewedLocation,
    supplier,
    sectionType,
    pricingAiProperties,
  },
});

/**
 * Sends the view event for a section of the open profile. The pricing section first waits
 * for the supplier's prices, so its event carries the pricing AI properties however early
 * it fires.
 */
export const viewedSupplierProfileSectionAnalyticsWithPricing =
  (params: IViewedSupplierProfileSectionAnalytics) =>
  ({ dispatch, getState, queryClient }: IDeps) => {
    if (params.viewedSection !== ANALYTICS_BY_SECTION_ID[PRICING_SECTION_ID]) {
      dispatch(viewedSupplierProfileSectionAnalytics(params));
      return noopAction();
    }

    // Taken now, so a profile opened while the prices load isn't reported instead.
    const supplier = params.supplier || getCurrentSupplier(getState());

    const supplierType = getSupplierTypeSlug(supplier);

    const supplierMarket = supplier.l10n?.country
      ? gazetteer.getMarketByCountry(supplier.l10n.country)
      : undefined;

    const { isEnabled } = resolveSupplierBucketSetting(
      'smartPricing',
      supplierType,
      supplierMarket,
    );

    if (!isEnabled) {
      dispatch(viewedSupplierProfileSectionAnalytics(params));
      return noopAction();
    }

    const asyncAction = async () => {
      const pricingAiProperties = await queryClient
        .fetchQuery(getSupplierPricingGroupedByYearQuery(supplier.id))
        .then((pricesByYear) =>
          getPricingAiProperties({
            countryCode: supplier.l10n?.country,
            supplierType,
            prices: Object.values(pricesByYear ?? {}).flat(),
          }),
        )
        // A failed read must not report the properties as off, so the event goes without them.
        .catch(() => undefined);

      dispatch(viewedSupplierProfileSectionAnalytics({ ...params, supplier, pricingAiProperties }));
    };
    asyncAction();
    return noopAction();
  };

export const clearSupplierDetails = () => ({
  type: SupplierActions.CLEAR_SUPPLIER_DETAILS,
});

export const setUpNextSuppliersActive = (active: boolean) => ({
  type: SupplierActions.SET_UPNEXT_SUPPLIERS_VISIBLE,
  payload: active,
});

export const clickedSupplierInstantBrochureAnalytics = (payload: {
  section: SupplierPricingBrochureAnalyticsSection;
  location: SupplierPricingBrochureAnalyticsLocation;
  cta: string;
  supplier: ISupplier | IUISupplier;
}) => ({
  type: SupplierActions.CLICKED_SUPPLIER_INSTANT_BROCHURE_ANALYTICS,
  payload,
});

export const interactedWithPricingAiAnalytics = (payload: {
  element: 'toggle' | 'cta' | 'graph';
  elementLabel?:
    | 'Edit'
    | 'Edit guest count'
    | 'Edit wedding date'
    | 'Save'
    | 'See more'
    | 'table'
    | 'graph';
  pricingDetails?: Record<string, unknown>;
  pricingEstimateMinimum?: number;
  pricingEstimateMaximum?: number;
}) => ({
  type: SupplierActions.INTERACTED_WITH_PRICING_AI,
  payload,
});
